import { useCallback, useEffect, useState } from 'react'
import Sprint, {
  SprintStatus,
  SprintStatusDisplayOrder,
} from '../../../../lib/functions/sprint'
import { dateV2Service } from '../../../../domain/value-object/DateV2'

export type SprintOption = {
  value: string
  name: string
  subText: string
  teamUuid: string
  status: SprintStatus
  startDate: string
  endDate: string
}

const formatSprintPeriod = (startDate: string, endDate: string): string => {
  const start = dateV2Service.fromString(startDate)
  const end = dateV2Service.fromString(endDate)
  if (!start || !end) return ''
  return `${dateV2Service.formatMonthAndDate(start)}〜${dateV2Service.formatMonthAndDate(end)}`
}

const getSprintStatusSortIndex = (status: SprintStatus): number =>
  SprintStatusDisplayOrder.indexOf(status)

// KanbanV2 の fetchSprintOptions と同じ並べ替えルール (ステータス順 → startDate 昇順)。
// SprintReportNew からも同じ画面体験を提供するためロジックを揃える。
export const useSprintOptions = (): {
  sprintOptions: SprintOption[]
  loaded: boolean
} => {
  const [sprintOptions, setSprintOptions] = useState<SprintOption[]>([])
  const [loaded, setLoaded] = useState(false)

  const fetchSprintOptions = useCallback(async () => {
    const response = await Sprint.getSprints({ statusList: [] })
    if (!response.json) {
      setLoaded(true)
      return
    }
    // response.json を直接 sort すると元の配列を破壊するため、複製してから並べ替える。
    const options: SprintOption[] = [...response.json]
      .sort((a, b) => {
        const statusDiff =
          getSprintStatusSortIndex(a.status) -
          getSprintStatusSortIndex(b.status)
        if (statusDiff !== 0) return statusDiff
        // 同じステータス内では開始日の昇順（古いものが上）。
        // SR の開始/終了スプリント選択では、時系列で並ぶほうが範囲指定しやすい。
        return a.startDate.localeCompare(b.startDate)
      })
      .map(sprint => ({
        value: sprint.uuid,
        name: sprint.name,
        subText: formatSprintPeriod(sprint.startDate, sprint.endDate),
        teamUuid: sprint.teamUuid,
        status: sprint.status,
        startDate: sprint.startDate,
        endDate: sprint.endDate,
      }))
    setSprintOptions(options)
    setLoaded(true)
  }, [])

  useEffect(() => {
    fetchSprintOptions()
  }, [fetchSprintOptions])

  return { sprintOptions, loaded }
}
